import { LayoutUtilProps, Size, Svg } from "../../types";
import { ComponentPropsWithoutRef, forwardRef } from "react";
import styles from "./Icon.module.scss";
import { useLayoutPropsUtil } from "../../internal/hooks";
import cx from "classnames";

/**
 * Props for the Icon component
 * @extends Omit<ComponentPropsWithoutRef<"span">, "children">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type IconProps = Omit<ComponentPropsWithoutRef<"span">, "children"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * Apply a specific color to the icon.
     */
    color?: string;

    /**
     * The size of the icon.
     * @default medium
     */
    size?: Extract<Size, "small" | "medium" | "large" | "xlarge">;

    /**
     * Assets passed in using svgr.
     */
    svg: Svg;

    /**
     * Whether the icon should inherit the color from its parent element.
     * @default false
     */
    inherit?: boolean;
  };

/**
 * Icon component for displaying SVG icons with consistent styling and sizing.
 *
 * Features:
 * - SVG icon rendering with consistent styling
 * - Four predefined sizes: small, medium, large, xlarge
 * - Custom color support with optional inheritance
 * - Layout utilities for positioning and spacing
 * - Support for all standard span element props
 * - Flexible color inheritance and custom color options
 * - Consistent sizing across different icon sets
 *
 * @example
 * import Close from "@servicetitan/anvil2/assets/icons/material/round/close.svg";
 *
 * <Icon svg={Close} size="medium" />
 *
 * @example
 * import Info from "@servicetitan/anvil2/assets/icons/material/round/info.svg";
 *
 * <Icon
 *   svg={Info}
 *   size="large"
 *   color="#007bff"
 *   inherit={false}
 * />
 */
export const Icon = forwardRef<HTMLSpanElement, IconProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    size = "medium",
    className,
    svg: Svg,
    inherit,
    style,
    color,
    ...rest
  } = componentProps;

  const iconClassNames = cx([styles["icon"]], className, {
    [styles["small"]]: size === "small",
    [styles["medium"]]: size === "medium",
    [styles["large"]]: size === "large",
    [styles["xlarge"]]: size === "xlarge",
  });

  const styleCombined = {
    color: color ? color : inherit ? "inherit" : undefined,
    ...style,
    ...layoutStyles,
  };

  return (
    <span
      className={iconClassNames}
      data-anv="icon"
      style={styleCombined}
      ref={ref}
      {...rest}
    >
      <Svg />
    </span>
  );
});

Icon.displayName = "Icon";
